import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import type { FileUploadInput } from 'src/data-capture/hooks/file-uploads/types';
import { useFileUploadActions } from 'src/data-capture/hooks/file-uploads/useFileUploadActions';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { SelectedFile } from '../types';

type UseUploadFilesArgs = {
  clientId?: string;
  familyId?: string;
  onUploaded: (uploadedFileIds: SelectedFile[]) => void;
};

export function useUploadFiles({ clientId, familyId, onUploaded }: UseUploadFilesArgs) {
  const { uploadFiles } = useFileUploadActions({ clientId });
  const queryClient = useQueryClient();
  const [isUploading, setIsUploading] = useState(false);

  const handleFileUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const fileList = event.target.files;

      if (!fileList || fileList.length === 0) {
        event.target.value = '';
        return;
      }

      setIsUploading(true);

      try {
        const payload: FileUploadInput[] = Array.from(fileList).map(file => ({
          file,
          isResolved: true,
        }));

        const result = await uploadFiles(payload);

        const uploaded = result.map(response => ({
          id: response.data.id,
          fileId: response.data.file.id,
          name: response.data.file.fileName ?? 'Uploading...',
        }));

        onUploaded(uploaded);

        if (clientId) {
          await queryClient.invalidateQueries({ queryKey: ['clients', clientId, 'all-documents'] });
        }
        if (familyId) {
          await queryClient.invalidateQueries({
            queryKey: ['families', familyId, 'all-documents'],
          });
        }
      } catch (error) {
        notifyAndLogError(
          'Failed to upload files',
          'An error occurred while uploading files. Please try again.',
          error,
        );
      } finally {
        setIsUploading(false);
        event.target.value = '';
      }
    },
    [onUploaded, uploadFiles, queryClient, clientId, familyId],
  );

  return { isUploading, handleFileUpload };
}
